// A labelled text input (FR-047, FR-048; research R12, R14): visible label, 44 px high, `input` radius,
// `input`-width `border` edge (`primary` while focused, `danger` on error), the error written under the field in `danger` and linked to the
// input (web `aria-describedby`, Android `accessibilityHint`), announced politely. `kind` sets the
// autofill hints password managers rely on.
import { forwardRef, type ReactNode, useId, useState } from "react";
import {
  Platform,
  StyleSheet,
  TextInput,
  type TextInputProps,
  View,
} from "react-native";

import { appFontFamily } from "@/theme/fonts";
import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";

import { useFormSubmit } from "./formContext";
import { Text } from "./Text";

export type FieldKind =
  | "name"
  | "email"
  | "tel"
  | "current-password"
  | "new-password"
  /** 003: a school code (upper case, no autofill). */
  | "code"
  /** 012: a search box (no autofill, no capitals). */
  | "search";

type KindProps = Pick<
  TextInputProps,
  | "autoComplete"
  | "keyboardType"
  | "autoCapitalize"
  | "autoCorrect"
  | "secureTextEntry"
>;

const isWeb = Platform.OS === "web";

/** Web `autocomplete` tokens and Android autofill hints per kind (research R12). */
export const AUTOFILL: Record<FieldKind, { web: string; android: string }> = {
  name: { web: "name", android: "name" },
  email: { web: "email", android: "email" },
  tel: { web: "tel-national", android: "tel" },
  "current-password": { web: "current-password", android: "password" },
  "new-password": { web: "new-password", android: "password-new" },
  code: { web: "off", android: "off" },
  search: { web: "off", android: "off" },
};

function kindProps(kind: FieldKind): KindProps {
  const autoComplete = (
    isWeb ? AUTOFILL[kind].web : AUTOFILL[kind].android
  ) as TextInputProps["autoComplete"];
  switch (kind) {
    case "name":
      return { autoComplete, autoCapitalize: "words", autoCorrect: false };
    case "email":
      return {
        autoComplete,
        keyboardType: "email-address",
        autoCapitalize: "none",
        autoCorrect: false,
      };
    case "tel":
      return { autoComplete, keyboardType: "phone-pad" };
    case "code":
      return { autoComplete, autoCapitalize: "characters", autoCorrect: false };
    case "search":
      return { autoComplete, autoCapitalize: "none", autoCorrect: false };
    default:
      return {
        autoComplete,
        autoCapitalize: "none",
        autoCorrect: false,
        secureTextEntry: true,
      };
  }
}

export type TextFieldProps = {
  label: string;
  value: string;
  onChangeText: (value: string) => void;
  kind: FieldKind;
  /** Shown under the field in `prt`; also turns the border `pr`. */
  error?: string | null;
  /** Help text under the label (for example the password rule, FR-005). */
  hint?: string;
  /** Overrides the kind's secure entry (PasswordField's show/hide). */
  secureTextEntry?: boolean;
  /** A control inside the field's right edge (PasswordField's toggle). */
  accessory?: ReactNode;
  autoFocus?: boolean;
  editable?: boolean;
  testID?: string;
};

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    field: { gap: t.space.xs },
    row: {
      flexDirection: "row",
      alignItems: "center",
      minHeight: t.size.input,
      borderWidth: t.borderWidth.input,
      borderColor: t.color.border,
      borderRadius: t.radius.input,
      backgroundColor: t.color.surface,
    },
    rowFocused: { borderColor: t.color.primary },
    rowError: { borderColor: t.color.danger },
    input: {
      flex: 1,
      minHeight: t.size.input,
      paddingHorizontal: t.space.md,
      color: t.color.text,
      fontFamily: appFontFamily(),
      fontSize: t.fontSize.base,
    },
    error: { color: t.color.danger },
    // The 6-character school code reads as a code: larger, bold, spaced (FR-018).
    code: {
      fontSize: t.fontSize.x2l,
      fontWeight: t.font.weight.bold,
      letterSpacing: t.letterSpacing.code,
      minHeight: t.size.buttonLg,
    },
    // The web draws its own outline on the input; the row's border is the focus state.
    noOutline: { outlineStyle: "none" } as object,
  }),
);

export const TextField = forwardRef<TextInput, TextFieldProps>(
  function TextField(
    {
      label,
      value,
      onChangeText,
      kind,
      error,
      hint,
      secureTextEntry,
      accessory,
      autoFocus,
      editable = true,
      testID,
    },
    ref,
  ) {
    const styles = useStyles();
    const theme = useTheme();
    const submit = useFormSubmit();
    const id = useId();
    const [focused, setFocused] = useState(false);
    const inputId = `field-${id}`;
    const hintId = `${inputId}-hint`;
    const errorId = `${inputId}-error`;
    const hasError = typeof error === "string" && error !== "";
    const describedBy =
      [hint ? hintId : null, hasError ? errorId : null]
        .filter(Boolean)
        .join(" ") || undefined;
    const base = kindProps(kind);

    return (
      <View style={styles.field}>
        <Text
          weight="medium"
          nativeID={`${inputId}-label`}
          {...(isWeb ? ({ htmlFor: inputId } as object) : {})}
        >
          {label}
        </Text>
        {hint ? (
          <Text tone="muted" size="sm" nativeID={hintId}>
            {hint}
          </Text>
        ) : null}
        <View
          style={[
            styles.row,
            focused && styles.rowFocused,
            hasError && styles.rowError,
          ]}
          testID={testID ? `${testID}-box` : undefined}
        >
          <TextInput
            ref={ref}
            {...base}
            {...(secureTextEntry === undefined ? {} : { secureTextEntry })}
            nativeID={inputId}
            value={value}
            onChangeText={onChangeText}
            accessibilityLabel={label}
            aria-labelledby={`${inputId}-label`}
            aria-invalid={hasError}
            aria-describedby={describedBy}
            accessibilityHint={hasError ? error : hint}
            importantForAutofill="yes"
            autoFocus={autoFocus}
            editable={editable}
            placeholderTextColor={theme.color.textMuted}
            // Android submits from the keyboard; the web <form> handles Enter itself.
            onSubmitEditing={isWeb ? undefined : submit}
            returnKeyType={isWeb ? undefined : "go"}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={[
              styles.input,
              kind === "code" && styles.code,
              isWeb && styles.noOutline,
            ]}
            testID={testID}
          />
          {accessory}
        </View>
        {hasError ? (
          <Text
            size="sm"
            nativeID={errorId}
            style={styles.error}
            accessibilityLiveRegion="polite"
            aria-live="polite"
          >
            {error}
          </Text>
        ) : null}
      </View>
    );
  },
);
